import {
  errorResult,
  normalizeSMSAppointmentNotificationTemplate,
  Result,
  SMSAppointmentNotificationTemplate,
  SMSAppointmentNotificationTemplateData,
  SMS_APPOINTMENT_NOTIFICATION_TEMPLATE_VERSION,
  Source,
  successResult,
  tu,
} from "beitary-shared";
import {
  collection,
  deleteDoc,
  doc,
  Firestore,
  onSnapshot,
  query,
  setDoc,
  Unsubscribe,
  updateDoc,
} from "firebase/firestore";

interface AddSMSAppointmentNotificationTemplate {
  ({
    db,
    organizationId,
    authorId,
    authorName,
    source,
    data,
  }: {
    db: Firestore;
    organizationId: string;
    authorId: string;
    authorName: string;
    source: Source;
    data: SMSAppointmentNotificationTemplateData;
  }): Promise<Result<boolean | null>>;
}

const addSMSAppointmentNotificationTemplate: AddSMSAppointmentNotificationTemplate =
  async ({ db, organizationId, authorId, authorName, source, data }) => {
    try {
      // create a new ref to get a new smsAppointmentNotificationTemplate id
      const newSMSAppointmentNotificationTemplateRef = doc(
        collection(
          db,
          "organizations",
          organizationId,
          "sms_appointment_notification_templates"
        )
      );

      // console.log(newSMSAppointmentNotificationTemplateRef);

      const newSMSAppointmentNotificationTemplate: SMSAppointmentNotificationTemplate =
        normalizeSMSAppointmentNotificationTemplate({
          ...data,
          id: newSMSAppointmentNotificationTemplateRef.id,
          authorId,
          authorName,
          version: SMS_APPOINTMENT_NOTIFICATION_TEMPLATE_VERSION,
          source,
          createdAt: tu.getCurrentDateTime(), // probably have a triggered functions write these fields?
          lastUpdatedAt: tu.getCurrentDateTime(),
        });

      await setDoc(
        newSMSAppointmentNotificationTemplateRef,
        newSMSAppointmentNotificationTemplate
      );

      // t("SMS_APPOINTMENT_NOTIFICATION_TEMPLATE_CREATED")
      const successMessage = "SMS_APPOINTMENT_NOTIFICATION_TEMPLATE_CREATED";
      return successResult({
        message: successMessage,
        payload: true,
      });
    } catch (err: any) {
      return errorResult({ message: err.message });
    }
  };

interface DeleteSMSAppointmentNotificationTemplate {
  ({
    db,
    organizationId,
    id,
  }: {
    db: Firestore;
    organizationId: string;
    id: string;
  }): Promise<Result<boolean | null>>;
}

const deleteSMSAppointmentNotificationTemplate: DeleteSMSAppointmentNotificationTemplate =
  async ({ db, organizationId, id }) => {
    try {
      const docRef = doc(
        db,
        "organizations",
        organizationId,
        "sms_appointment_notification_templates",
        id
      );

      try {
        await deleteDoc(docRef);
        // t("SMS_APPOINTMENT_NOTIFICATION_TEMPLATE_DELETED")
        const successMessage = "SMS_APPOINTMENT_NOTIFICATION_TEMPLATE_DELETED";
        return successResult({
          message: successMessage,
          payload: true,
        });
      } catch (error: any) {
        console.log(error);
        return errorResult({ message: error.message });
      }
    } catch (err: any) {
      console.log(err);
      return errorResult({ message: err.message });
    }
  };

interface UpdateSMSAppointmentNotificationTemplate {
  ({
    db,
    organizationId,
    authorId,
    authorName,
    id,
    source,
    data,
  }: {
    db: Firestore;
    organizationId: string;
    authorId: string;
    authorName: string;
    id: string;
    source: Source;
    data: Partial<SMSAppointmentNotificationTemplateData>;
  }): Promise<Result<boolean | null>>;
}

const updateSMSAppointmentNotificationTemplate: UpdateSMSAppointmentNotificationTemplate =
  async ({ db, organizationId, authorId, authorName, id, source, data }) => {
    try {
      const docRef = doc(
        db,
        "organizations",
        organizationId,
        "sms_appointment_notification_templates",
        id
      );

      const updates: Partial<SMSAppointmentNotificationTemplate> = {
        ...data,
        authorId,
        authorName,
        version: SMS_APPOINTMENT_NOTIFICATION_TEMPLATE_VERSION,
        source,
        lastUpdatedAt: tu.getCurrentDateTime(),
      };

      await updateDoc(docRef, updates);

      // t("SMS_APPOINTMENT_NOTIFICATION_TEMPLATE_UPDATED")
      const successMessage = "SMS_APPOINTMENT_NOTIFICATION_TEMPLATE_UPDATED";

      return successResult({
        message: successMessage,
        payload: true,
      });
    } catch (err: any) {
      console.log(err.message);
      return errorResult({ message: err.message });
    }
  };

// get organization smsAppointmentNotificationTemplates listener
interface GetSMSAppointmentNotificationTemplatesListenerCallback {
  (
    smsAppointmentNotificationTemplates: SMSAppointmentNotificationTemplate[]
  ): void;
}
interface GetSMSAppointmentNotificationTemplatesListener {
  db: Firestore;
  organizationId: string;
  callback: GetSMSAppointmentNotificationTemplatesListenerCallback;
}

const getSMSAppointmentNotificationTemplatesListener = ({
  db,
  organizationId,
  callback,
}: GetSMSAppointmentNotificationTemplatesListener): Unsubscribe => {
  try {
    const smsAppointmentNotificationTemplatesQuery = query(
      collection(
        db,
        "organizations",
        organizationId,
        "sms_appointment_notification_templates"
      )
    );
    return onSnapshot(
      smsAppointmentNotificationTemplatesQuery,
      (querySnapshot) => {
        const smsAppointmentNotificationTemplates: SMSAppointmentNotificationTemplate[] =
          [];
        querySnapshot.forEach((doc) => {
          smsAppointmentNotificationTemplates.push(
            normalizeSMSAppointmentNotificationTemplate(doc.data())
          );
        });
        callback(smsAppointmentNotificationTemplates);
      }
    );
  } catch (err: any) {
    console.log(err);
    return () => {};
  }
};

// we inject dependencies to improve testability
export const smsAppointmentNotificationTemplates = (
  db: Firestore,
  organizationId: string,
  authorId: string,
  authorName: string,
  source: Source
) => {
  return {
    addSMSAppointmentNotificationTemplate: (
      data: SMSAppointmentNotificationTemplateData
    ) =>
      addSMSAppointmentNotificationTemplate({
        db,
        organizationId,
        authorId,
        authorName,
        source,
        data,
      }),
    getSMSAppointmentNotificationTemplatesListener: (
      callback: GetSMSAppointmentNotificationTemplatesListenerCallback
    ) =>
      getSMSAppointmentNotificationTemplatesListener({
        db,
        organizationId,
        callback,
      }),
    updateSMSAppointmentNotificationTemplate: (
      id: string,
      data: Partial<SMSAppointmentNotificationTemplateData>
    ) =>
      updateSMSAppointmentNotificationTemplate({
        db,
        organizationId,
        authorId,
        authorName,
        id,
        source,
        data,
      }),
    deleteSMSAppointmentNotificationTemplate: (id: string) =>
      deleteSMSAppointmentNotificationTemplate({
        db,
        organizationId,
        id,
      }),
    archiveSMSAppointmentNotificationTemplate: (id: string) =>
      updateSMSAppointmentNotificationTemplate({
        db,
        organizationId,
        authorId,
        authorName,
        id,
        source,
        data: { status: "ARCHIVED" },
      }),
    dearchiveSMSAppointmentNotificationTemplate: (id: string) =>
      updateSMSAppointmentNotificationTemplate({
        db,
        organizationId,
        authorId,
        authorName,
        id,
        source,
        data: { status: "ACTIVE" },
      }),
  };
};
